/**
 * RevenueCatService — DrawLab subscription management.
 *
 * Handles the Host Pass subscription via RevenueCat on iOS and Android.
 * Three products: monthly, annual, lifetime. All grant the same entitlement
 * `drawlab_host_pass`.
 *
 * On web, this service is a no-op — the PaywallModal falls back to a
 * "download the app" flow. Web subscriptions will be added via Stripe in
 * a later phase.
 *
 * Authentication model:
 * - appUserID is the Firebase Auth UID from the DDD Auth Hub
 * - This means purchases are associated with the user's DDD account, not the device
 * - Cross-platform sync happens automatically (iOS purchase visible on Android)
 *
 * Entitlement flow (end-to-end):
 * 1. User taps "Get Host Pass" in the paywall
 * 2. Native payment sheet shows (App Store or Google Play)
 * 3. User completes purchase
 * 4. RevenueCat credits the entitlement to the user
 * 5. RevenueCat sends a webhook to auth-hub's revenueCatSubscriptionWebhook
 * 6. That webhook writes to users/{uid}/appSubscriptions/draw-lab in Firestore
 * 7. On the next token refresh, the user's JWT claims include hostPass=true
 * 8. The useEntitlements hook picks it up and unlocks features
 *
 * Australian English throughout.
 */

import { Capacitor } from '@capacitor/core';

// We use dynamic import for the Purchases SDK so the web bundle isn't
// forced to load a native-only library. This service is a pure no-op
// on web until the Purchases module is actually loaded.
let Purchases = null;
let LOG_LEVEL = null;
let PURCHASES_ERROR_CODE = null;

// RevenueCat API keys (public, safe to include in client)
const REVENUECAT_CONFIG = {
  ios: import.meta.env.VITE_REVENUECAT_IOS_KEY || '',
  android: import.meta.env.VITE_REVENUECAT_ANDROID_KEY || '',
};

// Product IDs — must match exactly in RevenueCat dashboard,
// App Store Connect, and Google Play Console
export const PRODUCTS = {
  MONTHLY: 'drawlab_host_monthly',
  ANNUAL: 'drawlab_host_annual',
  LIFETIME: 'drawlab_host_lifetime',
};

// Entitlement ID — one umbrella grant that any of the three products unlocks
export const ENTITLEMENT_ID = 'drawlab_host_pass';

// Offering ID — collection of packages shown in the paywall
export const OFFERING_ID = 'default_drawlab';

const isNative = () => Capacitor.isNativePlatform();

const getApiKey = () => {
  const platform = Capacitor.getPlatform();
  if (platform === 'ios') return REVENUECAT_CONFIG.ios;
  if (platform === 'android') return REVENUECAT_CONFIG.android;
  return null;
};

class RevenueCatServiceClass {
  constructor() {
    this.isInitialised = false;
    this.customerInfo = null;
    this.offerings = null;
    this.listeners = [];
  }

  // ── Initialisation ─────────────────────────────────────────────────────

  /**
   * Initialise RevenueCat SDK. Call once after the user's Firebase auth
   * is ready so we can use their UID as the appUserID.
   */
  async initialise(userId = null) {
    if (!isNative()) {
      console.log('[RevenueCat] Not on native platform, skipping');
      return false;
    }

    if (this.isInitialised) {
      console.log('[RevenueCat] Already initialised');
      return true;
    }

    const apiKey = getApiKey();
    if (!apiKey) {
      console.warn('[RevenueCat] No API key configured');
      return false;
    }

    try {
      // Dynamic import — only loads on native
      const mod = await import('@revenuecat/purchases-capacitor');
      Purchases = mod.Purchases;
      LOG_LEVEL = mod.LOG_LEVEL;
      PURCHASES_ERROR_CODE = mod.PURCHASES_ERROR_CODE;

      await Purchases.setLogLevel({
        level: import.meta.env.DEV ? LOG_LEVEL.DEBUG : LOG_LEVEL.INFO,
      });

      await Purchases.configure({
        apiKey,
        appUserID: userId || null,
      });

      this.isInitialised = true;
      console.log('[RevenueCat] Initialised successfully');

      // Fetch initial state
      await Promise.all([this.refreshCustomerInfo(), this.fetchOfferings()]);

      // Subscribe to customer info updates (e.g. after renewal)
      Purchases.addCustomerInfoUpdateListener((info) => {
        this.customerInfo = info.customerInfo;
        this.notifyListeners();
      });

      return true;
    } catch (err) {
      console.error('[RevenueCat] Initialisation failed:', err);
      return false;
    }
  }

  // ── User Management ────────────────────────────────────────────────────

  /**
   * Log in a user (sync purchases across devices).
   */
  async login(userId) {
    if (!this.isInitialised || !isNative() || !Purchases) return null;
    try {
      const { customerInfo } = await Purchases.logIn({ appUserID: userId });
      this.customerInfo = customerInfo;
      this.notifyListeners();
      return customerInfo;
    } catch (err) {
      console.error('[RevenueCat] Login failed:', err);
      return null;
    }
  }

  /**
   * Log out a user (e.g. on DDD sign-out).
   */
  async logout() {
    if (!this.isInitialised || !isNative() || !Purchases) return null;
    try {
      const { customerInfo } = await Purchases.logOut();
      this.customerInfo = customerInfo;
      this.notifyListeners();
      return customerInfo;
    } catch (err) {
      console.error('[RevenueCat] Logout failed:', err);
      return null;
    }
  }

  // ── Customer Info & Entitlements ───────────────────────────────────────

  async refreshCustomerInfo() {
    if (!this.isInitialised || !isNative() || !Purchases) return null;
    try {
      const { customerInfo } = await Purchases.getCustomerInfo();
      this.customerInfo = customerInfo;
      this.notifyListeners();
      return customerInfo;
    } catch (err) {
      console.error('[RevenueCat] Failed to get customer info:', err);
      return null;
    }
  }

  /**
   * Check if the user currently has Host Pass entitlement.
   * Returns false on web (use the auth-hub claim instead via useEntitlements).
   */
  hasHostPass() {
    if (!isNative() || !this.customerInfo) return false;
    const entitlement = this.customerInfo.entitlements?.active?.[ENTITLEMENT_ID];
    return !!entitlement;
  }

  /**
   * Get details of the active Host Pass entitlement (expiry, period, etc.).
   */
  getHostPassDetails() {
    if (!this.hasHostPass()) return null;
    const entitlement = this.customerInfo.entitlements.active[ENTITLEMENT_ID];
    return {
      identifier: entitlement.identifier,
      productIdentifier: entitlement.productIdentifier,
      isActive: entitlement.isActive,
      willRenew: entitlement.willRenew,
      expirationDate: entitlement.expirationDate || null,
      originalPurchaseDate: entitlement.originalPurchaseDate || null,
      periodType: entitlement.periodType, // 'normal' | 'intro' | 'trial'
    };
  }

  // ── Offerings & Products ───────────────────────────────────────────────

  /**
   * Fetch available offerings from RevenueCat.
   */
  async fetchOfferings() {
    if (!this.isInitialised || !isNative() || !Purchases) return null;
    try {
      const { offerings } = await Purchases.getOfferings();
      this.offerings = offerings;
      return offerings;
    } catch (err) {
      console.error('[RevenueCat] Failed to get offerings:', err);
      return null;
    }
  }

  /**
   * Get the three packages (monthly, annual, lifetime) from the current offering.
   * Returns an array with consistent ordering: [monthly, annual, lifetime].
   */
  getHostPassPackages() {
    const current = this.offerings?.current;
    if (!current || !current.availablePackages) return [];

    const byProductId = (id) =>
      current.availablePackages.find((p) => p.product?.identifier === id);

    const packages = [
      byProductId(PRODUCTS.MONTHLY),
      byProductId(PRODUCTS.ANNUAL),
      byProductId(PRODUCTS.LIFETIME),
    ].filter(Boolean);

    return packages;
  }

  /**
   * Get a specific package by its tier identifier ('monthly' | 'annual' | 'lifetime').
   */
  getPackageByTier(tier) {
    const productId = PRODUCTS[tier.toUpperCase()];
    if (!productId) return null;
    return this.getHostPassPackages().find(
      (pkg) => pkg.product?.identifier === productId,
    );
  }

  // ── Purchases ──────────────────────────────────────────────────────────

  /**
   * Purchase a Host Pass package.
   * @param {object} pkg - A RevenueCat Package object
   * @returns {Promise<{success: boolean, customerInfo?, error?, cancelled?}>}
   */
  async purchasePackage(pkg) {
    if (!this.isInitialised || !isNative() || !Purchases) {
      return { success: false, error: 'Purchases not available on this platform' };
    }

    if (!pkg) {
      return { success: false, error: 'No package provided' };
    }

    try {
      const { customerInfo } = await Purchases.purchasePackage({ aPackage: pkg });
      this.customerInfo = customerInfo;
      this.notifyListeners();

      if (this.hasHostPass()) {
        console.log('[RevenueCat] Host Pass purchased successfully');
        return { success: true, customerInfo };
      }

      return {
        success: false,
        error: 'Purchase completed but entitlement not active. Try restoring.',
      };
    } catch (err) {
      console.error('[RevenueCat] Purchase failed:', err);
      return this.handlePurchaseError(err);
    }
  }

  /**
   * Parse a purchase error into a user-friendly result.
   */
  handlePurchaseError(error) {
    if (!PURCHASES_ERROR_CODE) {
      return { success: false, error: error?.message || 'Purchase failed' };
    }

    if (error.code === PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR) {
      return { success: false, cancelled: true };
    }

    if (error.code === PURCHASES_ERROR_CODE.PRODUCT_ALREADY_PURCHASED_ERROR) {
      return {
        success: false,
        error: "You already own this. Tap 'Restore Purchases' to activate it.",
      };
    }

    if (error.code === PURCHASES_ERROR_CODE.NETWORK_ERROR) {
      return {
        success: false,
        error: 'Network error. Check your connection and try again.',
      };
    }

    if (error.code === PURCHASES_ERROR_CODE.PURCHASE_NOT_ALLOWED_ERROR) {
      return {
        success: false,
        error: 'Purchases are not allowed on this device.',
      };
    }

    if (error.code === PURCHASES_ERROR_CODE.PAYMENT_PENDING_ERROR) {
      return {
        success: false,
        error: "Payment is pending. You'll get access once it completes.",
      };
    }

    return {
      success: false,
      error: error?.message || 'Purchase failed. Please try again.',
    };
  }

  /**
   * Restore previous purchases (for users who reinstalled or changed devices).
   */
  async restorePurchases() {
    if (!this.isInitialised || !isNative() || !Purchases) {
      return { success: false, error: 'Not available on this platform' };
    }

    try {
      const { customerInfo } = await Purchases.restorePurchases();
      this.customerInfo = customerInfo;
      this.notifyListeners();

      return {
        success: true,
        customerInfo,
        hasHostPass: this.hasHostPass(),
      };
    } catch (err) {
      console.error('[RevenueCat] Restore failed:', err);
      return {
        success: false,
        error: err?.message || 'Failed to restore purchases',
      };
    }
  }

  // ── Listeners ──────────────────────────────────────────────────────────

  /**
   * Subscribe to customer info updates.
   * Returns an unsubscribe function.
   */
  addListener(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== callback);
    };
  }

  notifyListeners() {
    this.listeners.forEach((cb) => {
      try { cb(this.customerInfo); } catch { /* ignore */ }
    });
  }
}

// Singleton
export const revenueCat = new RevenueCatServiceClass();
export default revenueCat;
